import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import confetti from 'canvas-confetti';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import {
  Users,
  Phone,
  Clock,
  Bot,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Edit,
  Trash2,
  CheckCircle,
  AlertCircle,
  XCircle,
  X,
  Link2,
  DollarSign,
  ToggleLeft,
  ToggleRight,
  LayoutDashboard,
  Coins,
  AlertTriangle,
  ArrowUpRight,
  Sparkles,
  Folder,
  FolderPlus,
  FolderOpen,
  Copy,
  Check,
  Pencil,
  ChevronRight,
  PauseCircle,
  ExternalLink,
  Lock,
} from 'lucide-react';
import GHLCustomMenuLinkModal from '../components/GHLCustomMenuLinkModal';
import SubAccountDrawer from '../components/SubAccountDrawer';

const FOLDER_COLORS = ['#14b8a6', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444', '#64748b'];

const SubAccountsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { api } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showMenuFor, setShowMenuFor] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [creditAlerts, setCreditAlerts] = useState({});

  // Folders
  const [folders, setFolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState('all'); // 'all' | 'unfiled' | <folder_id>
  const [editingFolder, setEditingFolder] = useState(null);    // {folder_id, name, color} for popover
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState(FOLDER_COLORS[0]);
  // GHL Custom Menu Link modal — shown when the agency owner clicks the
  // "Custom Menu Link" button in the header. Generates a templated URL
  // they paste into GHL Settings → Custom Menu Links so a click from
  // inside a GHL sub-account deep-links into the matching Voxinity
  // sub-account dashboard. See GHLCustomMenuLinkModal.jsx for details.
  const [showGHLMenuLinkModal, setShowGHLMenuLinkModal] = useState(false);

  // LeadConnector connection status (batched)
  const [ghlStatuses, setGhlStatuses] = useState({});
  const [copiedLocId, setCopiedLocId] = useState(null);

  // Move-to-folder sub-menu
  const [moveMenuFor, setMoveMenuFor] = useState(null);

  // New sub-account form — with optional pricing + primary contact blocks
  const [newSubAccount, setNewSubAccount] = useState({
    name: '',
    rate_per_min: 0.15,
    upfront_fee: '',
    mrr: '',
    contact_name: '',
    contact_email: '',
  });
  const [showPricingBlock, setShowPricingBlock] = useState(false);
  const [showContactBlock, setShowContactBlock] = useState(false);
  // Success-toast state after creation — so agency can copy temp password
  const [inviteResult, setInviteResult] = useState(null);
  const [copiedInvite, setCopiedInvite] = useState(false);
  
  // Edit/Rebilling modal state
  // Edit Pricing modal (upfront + MRR)
  const [editingPricing, setEditingPricing] = useState(null);
  const [savingPricing, setSavingPricing] = useState(false);

  useEffect(() => {
    if (workspaceId) {
      fetchData();
    }
  }, [workspaceId]);

  const fetchData = async () => {
    try {
      const [wsResponse, saResponse, foldersResp, ghlResp] = await Promise.all([
        api.get(`/api/workspaces/${workspaceId}`),
        api.get(`/api/workspaces/${workspaceId}/sub-accounts`),
        api.get(`/api/workspaces/${workspaceId}/folders`).catch(() => ({ data: { folders: [] } })),
        api.get(`/api/workspaces/${workspaceId}/sub-accounts/ghl-status`).catch(() => ({ data: { statuses: {} } })),
      ]);
      setWorkspace(wsResponse.data);
      setSubAccounts(saResponse.data);
      setFolders(foldersResp.data?.folders || []);
      setGhlStatuses(ghlResp.data?.statuses || {});
      
      // Fetch credit health to highlight sub-accounts with issues
      try {
        const healthResp = await api.get(`/api/workspaces/${workspaceId}/sub-accounts/credit-health`);
        const alerts = {};
        (healthResp.data?.alerts || []).forEach(a => {
          alerts[a.sub_account_id] = a;
        });
        setCreditAlerts(alerts);
        // Mark alerts as seen — clears the sidebar badge
        sessionStorage.setItem(`credit_alerts_seen_${workspaceId}`, Date.now().toString());
      } catch (e) { /* silently fail */ }
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSubAccount = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: newSubAccount.name,
        rate_per_min: newSubAccount.rate_per_min,
        workspace_id: workspaceId,
      };
      const upfront = parseFloat(newSubAccount.upfront_fee);
      const mrr = parseFloat(newSubAccount.mrr);
      if (!Number.isNaN(upfront) || !Number.isNaN(mrr)) {
        payload.pricing = {
          upfront_fee: Number.isNaN(upfront) ? null : upfront,
          mrr: Number.isNaN(mrr) ? null : mrr,
        };
      }
      const email = (newSubAccount.contact_email || '').trim();
      if (email) {
        payload.primary_contact = {
          name: newSubAccount.contact_name || '',
          email,
          role: 'client_admin',
        };
      }
      const resp = await api.post(`/api/workspaces/${workspaceId}/sub-accounts`, payload);

      // Confetti celebration
      try {
        confetti({ particleCount: 80, spread: 75, origin: { y: 0.3 }, colors: ['#2dd4bf', '#a78bfa', '#fbbf24', '#f472b6'] });
      } catch (_) { /* confetti is non-critical */ }

      if (resp.data?.invite?.temp_password) {
        // Keep the modal open in "success" state so agency can copy the temp password
        setInviteResult(resp.data.invite);
      } else {
        setShowAddModal(false);
      }
      setNewSubAccount({ name: '', rate_per_min: 0.15, upfront_fee: '', mrr: '', contact_name: '', contact_email: '' });
      setShowPricingBlock(false);
      setShowContactBlock(false);
      toast.success('Sub-account created');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create sub-account');
    }
  };

  const handleDeleteSubAccount = async (subAccountId) => {
    if (!window.confirm('Are you sure you want to delete this sub-account?')) return;
    try {
      await api.delete(`/api/sub-accounts/${subAccountId}`);
      toast.success('Sub-account deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete sub-account');
    }
    setShowMenuFor(null);
  };

  const handleUpdateStatus = async (subAccountId, newStatus) => {
    try {
      await api.put(`/api/sub-accounts/${subAccountId}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSavePricing = async (e) => {
    e.preventDefault();
    if (!editingPricing) return;
    setSavingPricing(true);
    try {
      const upfront = parseFloat(editingPricing.upfront_fee);
      const mrr = parseFloat(editingPricing.mrr);
      await api.patch(`/api/sub-accounts/${editingPricing.sub_account_id}/pricing`, {
        upfront_fee: Number.isNaN(upfront) ? null : upfront,
        mrr: Number.isNaN(mrr) ? null : mrr,
      });
      toast.success('Pricing updated');
      setEditingPricing(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const handleArchiveSubAccount = async (subAccountId) => {
    try {
      await api.put(`/api/sub-accounts/${subAccountId}`, { status: 'archived' });
      toast.success('Sub-account archived');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive sub-account');
    }
    setShowMenuFor(null);
  };

  const handleRestoreSubAccount = async (subAccountId) => {
    try {
      await api.put(`/api/sub-accounts/${subAccountId}`, { status: 'active' });
      toast.success('Sub-account restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore sub-account');
    }
    setShowMenuFor(null);
  };

  const filteredSubAccounts = subAccounts.filter(sa => {
    const matchesSearch = sa.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sa.status === statusFilter
                          || (statusFilter === 'paused' && sa.status === 'inactive'); // legacy
    let matchesFolder = true;
    if (activeFolderId === 'unfiled') matchesFolder = !sa.folder_id;
    else if (activeFolderId !== 'all') matchesFolder = sa.folder_id === activeFolderId;
    return matchesSearch && matchesStatus && matchesFolder;
  });

  const getStatusBadge = (status) => {
    const configs = {
      active: { icon: CheckCircle, color: 'text-green-400 bg-green-400/10', label: 'Active' },
      low_activity: { icon: AlertCircle, color: 'text-yellow-400 bg-yellow-400/10', label: 'Low Activity' },
      paused: { icon: PauseCircle, color: 'text-amber-400 bg-amber-400/10', label: 'Paused' },
      inactive: { icon: PauseCircle, color: 'text-amber-400 bg-amber-400/10', label: 'Paused' }, // legacy fallback
      archived: { icon: X, color: 'text-red-400 bg-red-400/10', label: 'Archived' }
    };
    const config = configs[status] || configs.paused;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  // ─────── Folder helpers ───────
  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error('Folder name required'); return; }
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/folders`, { name, color: newFolderColor });
      setFolders((f) => [...f, res.data]);
      setActiveFolderId(res.data.folder_id);
      setShowNewFolder(false);
      setNewFolderName('');
      setNewFolderColor(FOLDER_COLORS[0]);
      toast.success('Folder created');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create folder'); }
  };

  const saveFolderEdit = async () => {
    if (!editingFolder) return;
    try {
      await api.patch(`/api/workspaces/${workspaceId}/folders/${editingFolder.folder_id}`, {
        name: editingFolder.name, color: editingFolder.color,
      });
      setFolders((list) => list.map((f) => f.folder_id === editingFolder.folder_id ? { ...f, ...editingFolder } : f));
      setEditingFolder(null);
      toast.success('Folder updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removeFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Sub-accounts inside will move back to Unfiled.')) return;
    try {
      await api.delete(`/api/workspaces/${workspaceId}/folders/${folderId}`);
      setFolders((list) => list.filter((f) => f.folder_id !== folderId));
      setEditingFolder(null);
      if (activeFolderId === folderId) setActiveFolderId('all');
      // Update local sub-accounts that referenced this folder
      setSubAccounts((list) => list.map((sa) => sa.folder_id === folderId ? { ...sa, folder_id: null } : sa));
      toast.success('Folder deleted');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const moveToFolder = async (subAccountId, folderId) => {
    try {
      await api.patch(`/api/sub-accounts/${subAccountId}/folder`, { folder_id: folderId });
      setSubAccounts((list) => list.map((sa) => sa.sub_account_id === subAccountId ? { ...sa, folder_id: folderId } : sa));
      const fname = folderId ? (folders.find(f => f.folder_id === folderId)?.name || 'folder') : 'Unfiled';
      toast.success(`Moved to ${fname}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to move'); }
    setMoveMenuFor(null);
  };

  const copyLocId = (locId) => {
    navigator.clipboard.writeText(locId);
    setCopiedLocId(locId);
    toast.success('Location ID copied');
    setTimeout(() => setCopiedLocId(null), 1500);
  };

  const maskLocId = (locId) => {
    if (!locId || locId.length <= 9) return locId || '';
    return `${locId.slice(0, 4)}…${locId.slice(-3)}`;
  };

  // Calculate totals
  const totalClients = subAccounts.length;
  const activeClients = subAccounts.filter(sa => sa.status === 'active').length;
  const nonArchivedCount = subAccounts.filter(sa => sa.status !== 'archived').length;
  const totalCalls = subAccounts.reduce((sum, sa) => sum + (sa.calls || 0), 0);
  const totalMinutes = subAccounts.reduce((sum, sa) => sum + (sa.minutes || 0), 0);
  const subAccountLimit = workspace?.sub_account_limit ?? 1;
  const atLimit = nonArchivedCount >= subAccountLimit;
  const planLabel = (workspace?.plan || 'launchpad') === 'launchpad' ? 'Pay as you go' : (workspace?.plan || '').charAt(0).toUpperCase() + (workspace?.plan || '').slice(1);
  const goChangePlan = () => { setShowUpgradeModal(false); navigate(`/workspace/${workspaceId}/billing?change_plan=1`); };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-10 h-10 border-4 border-teal-400/30 border-t-teal-400 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="sub-accounts-page">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-display font-semibold text-white mb-1">
                Sub-Accounts
              </h1>
              <p className="text-gray-400">{workspace?.name}</p>
            </div>
            <div className="flex items-center gap-3">
              {subAccountLimit < 999 && (
                <span data-testid="sub-account-quota-badge" className={`text-xs px-2.5 py-1 rounded-lg font-medium ${atLimit ? 'bg-red-500/10 text-red-400 border border-red-500/20' : 'bg-white/5 text-gray-400 border border-white/10'}`}>
                  {nonArchivedCount}/{subAccountLimit}
                </span>
              )}
              <button
                onClick={() => setShowGHLMenuLinkModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all bg-white/5 hover:bg-white/10 text-white border border-white/10"
                data-testid="ghl-custom-menu-link-btn"
                title="Generate a GoHighLevel Custom Menu Link that deep-links your GHL sub-accounts to Voxinity"
              >
                <ExternalLink size={18} />
                <span className="hidden sm:inline">Custom Menu Link</span>
              </button>
              <button
                onClick={() => setShowNewFolder(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all bg-white/5 hover:bg-white/10 text-white border border-white/10"
                data-testid="add-folder-btn"
                title="Create a folder to organize sub-accounts"
              >
                <FolderPlus size={18} />
                <span className="hidden sm:inline">New Folder</span>
              </button>
              <button
                onClick={() => atLimit ? setShowUpgradeModal(true) : setShowAddModal(true)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${atLimit ? 'bg-gray-600/50 text-gray-400 cursor-not-allowed' : 'bg-teal-400 text-space-900 hover:bg-teal-500'}`}
                data-testid="add-sub-account-btn"
              >
                <Plus size={18} />
                <span className="hidden sm:inline">New Sub-Account</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <Users className="text-teal-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">{totalClients}</p>
                <p className="text-xs text-gray-400">Total Clients</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <CheckCircle className="text-green-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">{activeClients}</p>
                <p className="text-xs text-gray-400">Active Clients</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <Phone className="text-violet-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">{totalCalls.toLocaleString()}</p>
                <p className="text-xs text-gray-400">Total Calls</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-xl p-4"
          >
            <div className="flex items-center gap-3">
              <Clock className="text-phosphor-400" size={20} />
              <div>
                <p className="text-2xl font-semibold text-white">{totalMinutes.toFixed(0)}</p>
                <p className="text-xs text-gray-400">Total Minutes</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card rounded-xl overflow-hidden"
        >
          {/* Folders row — high-end glowing chips */}
          <div className="px-4 pt-4 pb-2 border-b border-white/5" data-testid="folders-row">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setActiveFolderId('all')}
                className={`group relative px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all border ${activeFolderId === 'all' ? 'bg-white/[0.07] border-white/20 text-white' : 'bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.05]'}`}
                data-testid="folder-chip-all"
              >
                All <span className="ml-1.5 text-[10px] opacity-70">{subAccounts.filter(s=>s.status!=='archived').length}</span>
              </button>
              <button
                onClick={() => setActiveFolderId('unfiled')}
                className={`group relative px-3.5 py-1.5 rounded-xl text-xs font-medium transition-all border ${activeFolderId === 'unfiled' ? 'bg-white/[0.07] border-white/20 text-white' : 'bg-white/[0.02] border-white/[0.06] text-gray-400 hover:text-white hover:bg-white/[0.05]'}`}
                data-testid="folder-chip-unfiled"
              >
                Unfiled <span className="ml-1.5 text-[10px] opacity-70">{subAccounts.filter(s=>!s.folder_id && s.status!=='archived').length}</span>
              </button>
              {folders.map((f) => {
                const count = subAccounts.filter(s => s.folder_id === f.folder_id && s.status !== 'archived').length;
                const active = activeFolderId === f.folder_id;
                return (
                  <div key={f.folder_id} className="relative group">
                    <button
                      onClick={() => setActiveFolderId(f.folder_id)}
                      onDoubleClick={() => setEditingFolder({ ...f })}
                      className="relative px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all border flex items-center gap-2"
                      style={{
                        background: active ? `${f.color}22` : `${f.color}10`,
                        borderColor: active ? `${f.color}66` : `${f.color}28`,
                        color: active ? '#fff' : f.color,
                        boxShadow: active ? `0 0 18px -3px ${f.color}80, inset 0 0 0 1px ${f.color}33` : `0 0 10px -4px ${f.color}66`,
                      }}
                      data-testid={`folder-chip-${f.folder_id}`}
                    >
                      <Folder size={11} style={{ color: f.color }} />
                      <span>{f.name}</span>
                      <span className="text-[10px] opacity-70">{count}</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingFolder({ ...f }); }}
                      className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#0c0d12] border border-white/10 text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      data-testid={`folder-edit-${f.folder_id}`}
                      title="Edit folder"
                    >
                      <Pencil size={9} />
                    </button>
                  </div>
                );
              })}
              {showNewFolder ? (
                <div className="flex items-center gap-1.5 bg-white/[0.04] border border-white/15 rounded-xl pl-2 pr-1 py-1">
                  <input
                    autoFocus value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); } }}
                    placeholder="Folder name" className="bg-transparent text-xs text-white placeholder-gray-500 focus:outline-none w-28"
                    data-testid="new-folder-input"
                  />
                  <div className="flex items-center gap-0.5">
                    {FOLDER_COLORS.map(c => (
                      <button key={c} type="button" onClick={() => setNewFolderColor(c)}
                        className={`w-3.5 h-3.5 rounded-full border ${newFolderColor === c ? 'border-white scale-110' : 'border-transparent'} transition-transform`}
                        style={{ background: c, boxShadow: newFolderColor === c ? `0 0 8px ${c}` : 'none' }}
                      />
                    ))}
                  </div>
                  <button onClick={createFolder} className="p-1 rounded-lg bg-teal-400 text-space-900 hover:bg-teal-500" data-testid="new-folder-confirm"><Check size={12} /></button>
                  <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="p-1 rounded-lg text-gray-400 hover:bg-white/10"><X size={12} /></button>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewFolder(true)}
                  className="px-3 py-1.5 rounded-xl text-xs font-medium text-gray-400 hover:text-white hover:bg-white/[0.04] border border-dashed border-white/10 transition-all flex items-center gap-1.5"
                  data-testid="add-folder-inline"
                >
                  <Plus size={12} /> Add folder
                </button>
              )}
            </div>
          </div>

          {/* Filters */}
          <div className="p-4 border-b border-white/5 flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-teal-400/50"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="low_activity">Low Activity</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Client Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">LeadConnector</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Re-billing</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredSubAccounts.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-4 py-12 text-center">
                      <Users className="mx-auto text-gray-600 mb-3" size={32} />
                      <p className="text-gray-400 text-sm">No sub-accounts found</p>
                    </td>
                  </tr>
                ) : (
                  filteredSubAccounts.map((account) => (
                    <tr 
                      key={account.sub_account_id} 
                      className={`hover:bg-white/5 transition-colors cursor-pointer ${
                        creditAlerts[account.sub_account_id]?.status === 'zero' ? 'bg-red-500/[0.04] border-l-2 border-l-red-500' :
                        creditAlerts[account.sub_account_id]?.status === 'low' ? 'bg-amber-400/[0.03] border-l-2 border-l-amber-400' : ''
                      }`}
                      onClick={() => account.status === 'archived' ? setShowMenuFor(account.sub_account_id) : window.open(`/workspace/${workspaceId}/sub-account/${account.sub_account_id}`, '_blank')}
                      data-testid={`sub-account-row-${account.sub_account_id}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-medium text-sm ${
                            creditAlerts[account.sub_account_id]?.status === 'zero' ? 'bg-gradient-to-br from-red-500/40 to-red-600/40' :
                            creditAlerts[account.sub_account_id]?.status === 'low' ? 'bg-gradient-to-br from-amber-400/40 to-orange-500/40' :
                            'bg-gradient-to-br from-violet-400/30 to-teal-400/30'
                          }`}>
                            {account.name?.charAt(0) || 'S'}
                          </div>
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white">{account.name}</p>
                            {account.status === 'archived' && account.archived_reason === 'plan_limit' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-400/15 text-amber-400 border border-amber-400/20 hover:bg-amber-400/25"
                                data-testid={`locked-badge-${account.sub_account_id}`}
                              >
                                <Lock size={9} /> Over plan limit — Upgrade to unlock
                              </button>
                            )}
                            {creditAlerts[account.sub_account_id]?.status === 'zero' && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/15 text-red-400 border border-red-500/20" data-testid={`credit-alert-${account.sub_account_id}`}>
                                <Coins size={9} /> No Credits
                              </span>
                            )}
                            {creditAlerts[account.sub_account_id]?.status === 'low' && (
                              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-400/15 text-amber-400 border border-amber-400/20" data-testid={`credit-alert-${account.sub_account_id}`}>
                                <AlertTriangle size={9} /> Low Credits
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {getStatusBadge(account.status)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {(() => {
                          const g = ghlStatuses[account.sub_account_id];
                          if (g && g.connected) {
                            return (
                              <div className="flex items-center gap-2" data-testid={`ghl-connected-${account.sub_account_id}`}>
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-green-400/10 text-green-400 border border-green-400/20">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]" />
                                  Connected
                                </span>
                                <button
                                  onClick={() => copyLocId(g.location_id)}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/5 hover:bg-white/10 text-[11px] font-mono text-gray-300 border border-white/5 transition-colors"
                                  title={g.location_id}
                                  data-testid={`ghl-loc-copy-${account.sub_account_id}`}
                                >
                                  {copiedLocId === g.location_id ? <Check size={10} className="text-green-400" /> : <Copy size={10} />}
                                  {maskLocId(g.location_id)}
                                </button>
                              </div>
                            );
                          }
                          return (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium bg-white/5 text-gray-500 border border-white/5" data-testid={`ghl-disconnected-${account.sub_account_id}`}>
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                              Not Connected
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {account.rebilling_enabled ? (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold text-green-400 bg-green-400/10 border border-green-400/20" data-testid={`rebilling-on-${account.sub_account_id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_rgba(74,222,128,0.7)]" />
                            ON {account.rebilling_rate ? <span className="text-[10px] font-mono opacity-80">· ${Number(account.rebilling_rate).toFixed(2)}/min</span> : null}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold text-gray-500 bg-white/5 border border-white/5" data-testid={`rebilling-off-${account.sub_account_id}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-gray-500" />
                            OFF
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-right relative" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => setShowMenuFor(showMenuFor === account.sub_account_id ? null : account.sub_account_id)}
                          className="p-2 rounded-lg hover:bg-white/10 text-gray-400 transition-colors"
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>

      {/* Sub-Account Settings Drawer — slides out from the right */}
      {showMenuFor && (() => {
        const account = subAccounts.find(sa => sa.sub_account_id === showMenuFor);
        if (!account) return null;
        return (
          <SubAccountDrawer
            key={account.sub_account_id}
            account={account}
            workspaceId={workspaceId}
            api={api}
            folders={folders}
            atLimit={atLimit}
            onClose={() => setShowMenuFor(null)}
            onChanged={fetchData}
            onEditPricing={(acc) => setEditingPricing({ sub_account_id: acc.sub_account_id, name: acc.name, upfront_fee: acc.pricing?.upfront_fee ?? '', mrr: acc.pricing?.mrr ?? '' })}
            onUpdateStatus={handleUpdateStatus}
            onArchive={handleArchiveSubAccount}
            onRestore={handleRestoreSubAccount}
            onMoveToFolder={moveToFolder}
            onUpgrade={() => setShowUpgradeModal(true)}
          />
        );
      })()}

      {/* Add Sub-Account Modal — with optional Pricing + Primary Contact blocks */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-space-800 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white">{inviteResult ? 'Sub-Account Created' : 'New Sub-Account'}</h3>
              <button
                onClick={() => { setShowAddModal(false); setInviteResult(null); }}
                className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="close-add-sub-account-modal"
              >
                <X size={18} />
              </button>
            </div>

            {inviteResult ? (
              // SUCCESS STATE — show temp password for agency to share
              <div className="space-y-4" data-testid="invite-result">
                <div className="p-4 rounded-xl bg-gradient-to-br from-emerald-400/10 to-teal-400/5 border border-emerald-400/30">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="text-emerald-400" size={18} />
                    <p className="text-sm font-semibold text-emerald-300">Client invite ready</p>
                  </div>
                  <p className="text-xs text-gray-400 mb-3">Share these login credentials with your client. They'll be required to set a new password on first login.</p>
                  <div className="space-y-2 text-xs">
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/30">
                      <span className="text-gray-500">Email</span>
                      <code className="text-white font-mono">{inviteResult.email}</code>
                    </div>
                    <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-black/30">
                      <span className="text-gray-500">Temp password</span>
                      <code className="text-amber-300 font-mono font-bold tracking-wider">{inviteResult.temp_password}</code>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-3 leading-relaxed">
                    This client has been added to your Members list. You can manage them anytime from <b className="text-gray-400">Sub-Account Settings → Members</b>.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const text = `Email: ${inviteResult.email}\nTemp password: ${inviteResult.temp_password}`;
                      navigator.clipboard?.writeText(text);
                      setCopiedInvite(true);
                      toast.success('Copied to clipboard');
                      setTimeout(() => setCopiedInvite(false), 2000);
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors"
                    data-testid="copy-invite-btn"
                  >
                    {copiedInvite ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy Credentials</>}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowAddModal(false); setInviteResult(null); }}
                    className="px-4 py-3 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleAddSubAccount} className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Business Name <span className="text-rose-400">*</span></label>
                  <input
                    type="text"
                    value={newSubAccount.name}
                    onChange={(e) => setNewSubAccount({ ...newSubAccount, name: e.target.value })}
                    placeholder="e.g. Roofing Pros"
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
                    required
                    data-testid="new-sub-account-name"
                  />
                </div>

                {/* Optional Pricing block */}
                <div className="rounded-xl border border-white/10 bg-white/[0.02]">
                  <button
                    type="button"
                    onClick={() => setShowPricingBlock(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                    data-testid="toggle-pricing-block"
                  >
                    <div className="flex items-center gap-2">
                      <DollarSign size={15} className="text-emerald-400" />
                      <span className="text-sm font-medium text-white">Client Deal Value</span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-500">Optional</span>
                    </div>
                    <ChevronRight size={15} className={`text-gray-500 transition-transform ${showPricingBlock ? 'rotate-90' : ''}`} />
                  </button>
                  {showPricingBlock && (
                    <div className="px-4 pb-4 space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Upfront fee (one-time)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                          <input
                            type="number" min="0" step="0.01"
                            value={newSubAccount.upfront_fee}
                            onChange={(e) => setNewSubAccount({ ...newSubAccount, upfront_fee: e.target.value })}
                            placeholder="e.g. 500"
                            className="w-full pl-7 pr-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 text-sm"
                            data-testid="new-sub-account-upfront"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Monthly recurring (MRR)</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                          <input
                            type="number" min="0" step="0.01"
                            value={newSubAccount.mrr}
                            onChange={(e) => setNewSubAccount({ ...newSubAccount, mrr: e.target.value })}
                            placeholder="e.g. 200"
                            className="w-full pl-7 pr-14 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-teal-400/50 text-sm"
                            data-testid="new-sub-account-mrr"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">/mo</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-500">Rolls up into your Agency Dashboard ROI snapshot.</p>
                    </div>
                  )}
                </div>

                {/* Optional Primary Contact block */}
                <div className="rounded-xl border border-white/10 bg-white/[0.02]">
                  <button
                    type="button"
                    onClick={() => setShowContactBlock(v => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left"
                    data-testid="toggle-contact-block"
                  >
                    <div className="flex items-center gap-2">
                      <Users size={15} className="text-violet-400" />
                      <span className="text-sm font-medium text-white">Primary Client Contact</span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-500">Optional</span>
                    </div>
                    <ChevronRight size={15} className={`text-gray-500 transition-transform ${showContactBlock ? 'rotate-90' : ''}`} />
                  </button>
                  {showContactBlock && (
                    <div className="px-4 pb-4 space-y-3">
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Client Name</label>
                        <input
                          type="text"
                          value={newSubAccount.contact_name}
                          onChange={(e) => setNewSubAccount({ ...newSubAccount, contact_name: e.target.value })}
                          placeholder="e.g. John Smith"
                          className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-violet-400/50 text-sm"
                          data-testid="new-sub-account-contact-name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-400 mb-1.5">Client Email</label>
                        <input
                          type="email"
                          value={newSubAccount.contact_email}
                          onChange={(e) => setNewSubAccount({ ...newSubAccount, contact_email: e.target.value })}
                          placeholder="client@theirbusiness.com"
                          className="w-full px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-violet-400/50 text-sm"
                          data-testid="new-sub-account-contact-email"
                        />
                      </div>
                      <p className="text-[10px] text-gray-500">We'll generate a temp password. Client must reset it on first login.</p>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-3 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-3 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors"
                    data-testid="submit-new-sub-account"
                  >
                    Create
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}

      {/* Edit Pricing Modal */}
      {editingPricing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="edit-pricing-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md bg-space-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="text-lg font-semibold text-white flex items-center gap-2"><DollarSign size={18} className="text-emerald-400" /> Edit Pricing</h3>
                <p className="text-xs text-gray-500 mt-0.5">{editingPricing.name}</p>
              </div>
              <button onClick={() => setEditingPricing(null)} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSavePricing} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Upfront fee (one-time)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={editingPricing.upfront_fee ?? ''}
                    onChange={(e) => setEditingPricing({ ...editingPricing, upfront_fee: e.target.value })}
                    placeholder="Leave empty to clear"
                    className="w-full pl-7 pr-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-emerald-400/50 text-sm"
                    data-testid="edit-pricing-upfront"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Monthly recurring (MRR)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                  <input
                    type="number" min="0" step="0.01"
                    value={editingPricing.mrr ?? ''}
                    onChange={(e) => setEditingPricing({ ...editingPricing, mrr: e.target.value })}
                    placeholder="Leave empty to clear"
                    className="w-full pl-7 pr-14 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:border-teal-400/50 text-sm"
                    data-testid="edit-pricing-mrr"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-xs">/mo</span>
                </div>
              </div>
              <p className="text-[10px] text-gray-500">Changes apply immediately to your ROI snapshot.</p>
              <div className="flex items-center gap-3 pt-2">
                <button type="button" onClick={() => setEditingPricing(null)}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={savingPricing}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-teal-400 text-space-900 font-medium hover:bg-teal-500 transition-colors disabled:opacity-50"
                  data-testid="save-pricing-btn">
                  {savingPricing ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit Sub-Account Modal with Re-billing */}
      {/* Upgrade Plan Modal */}
      {showUpgradeModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowUpgradeModal(false); }}
          data-testid="upgrade-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            className="w-full max-w-lg rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0c0d12] shadow-2xl overflow-hidden"
            data-testid="upgrade-modal"
          >
            {/* Header */}
            <div className="px-6 pt-6 pb-4">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-400/20 to-violet-400/20 flex items-center justify-center">
                  <Sparkles className="text-teal-400" size={18} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Upgrade to add more sub-accounts</h3>
                  <p className="text-xs text-gray-500" data-testid="upgrade-modal-usage">You've used {nonArchivedCount} of {subAccountLimit} sub-account{subAccountLimit === 1 ? '' : 's'} on the {planLabel} plan. Upgrade your plan to add more.</p>
                </div>
              </div>
            </div>

            {/* Plan Options */}
            <div className="px-6 pb-4 space-y-2">
              {[
                { id: 'launchpad', name: 'Free', price: 0, subs: 1, color: 'gray' },
                { id: 'starter', name: 'Starter', price: 197, subs: 3, color: 'teal' },
                { id: 'agency', name: 'Agency', price: 397, subs: 10, color: 'blue' },
                { id: 'pro', name: 'Pro', price: 897, subs: 'Unlimited', color: 'violet' },
              ].filter(p => {
                const currentLimit = subAccountLimit;
                const planLimit = p.subs === 'Unlimited' ? 999 : p.subs;
                return planLimit > currentLimit;
              }).map(plan => {
                const isBest = plan.id === 'pro';
                return (
                  <button
                    key={plan.id}
                    onClick={goChangePlan}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all text-left group hover:scale-[1.01] ${
                      isBest
                        ? 'border-violet-400/30 bg-violet-500/[0.06] hover:bg-violet-500/[0.1]'
                        : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.05]'
                    }`}
                    data-testid={`upgrade-${plan.id}-btn`}
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                      plan.color === 'teal' ? 'bg-teal-400/15' :
                      plan.color === 'blue' ? 'bg-blue-400/15' :
                      plan.color === 'violet' ? 'bg-violet-400/15' : 'bg-gray-400/15'
                    }`}>
                      <Users size={18} className={
                        plan.color === 'teal' ? 'text-teal-400' :
                        plan.color === 'blue' ? 'text-blue-400' :
                        plan.color === 'violet' ? 'text-violet-400' : 'text-gray-400'
                      } />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-white">{plan.name}</span>
                        {isBest && <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-400/20 text-violet-600 dark:text-violet-300 font-bold uppercase">Best</span>}
                      </div>
                      <p className="text-xs text-gray-500">{plan.subs === 'Unlimited' ? 'Unlimited' : `Up to ${plan.subs}`} sub-accounts</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-white">${plan.price}<span className="text-xs text-gray-500 font-normal">/mo</span></p>
                    </div>
                    <ArrowUpRight size={16} className="text-gray-600 group-hover:text-teal-400 transition-colors shrink-0" />
                  </button>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/[0.04] space-y-2">
              <button
                onClick={goChangePlan}
                className="w-full py-2.5 rounded-xl text-sm font-semibold bg-teal-400 text-space-900 hover:bg-teal-500 transition-colors"
                data-testid="upgrade-modal-change-plan"
              >
                Change Plan
              </button>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="w-full py-2.5 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/[0.04] transition-colors"
                data-testid="upgrade-modal-close"
              >
                Maybe Later
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Folder Edit Popover */}
      {editingFolder && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setEditingFolder(null)}
          data-testid="folder-edit-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="w-full max-w-sm rounded-2xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-[#0c0d12] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="folder-edit-modal"
          >
            <div className="px-5 pt-5 pb-3">
              <p className="text-sm font-semibold text-white">Edit Folder</p>
              <p className="text-[11px] text-gray-500">Rename or change the color</p>
            </div>
            <div className="px-5 space-y-4">
              <div>
                <label className="text-xs text-gray-400 font-medium mb-1.5 block">Name</label>
                <input
                  value={editingFolder.name}
                  onChange={(e) => setEditingFolder({ ...editingFolder, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveFolderEdit(); }}
                  className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-white text-sm focus:outline-none focus:border-teal-400/50"
                  data-testid="folder-edit-name"
                />
              </div>
              <div>
                <label className="text-xs text-gray-400 font-medium mb-2 block">Color</label>
                <div className="flex items-center gap-2">
                  {FOLDER_COLORS.map(c => (
                    <button key={c} type="button" onClick={() => setEditingFolder({ ...editingFolder, color: c })}
                      className={`w-7 h-7 rounded-lg border-2 transition-all ${editingFolder.color === c ? 'border-white scale-110' : 'border-transparent hover:scale-105'}`}
                      style={{ background: c, boxShadow: editingFolder.color === c ? `0 0 12px ${c}` : 'none' }}
                      data-testid={`folder-edit-color-${c.replace('#','')}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <div className="mt-5 px-5 py-4 border-t border-white/[0.05] flex items-center justify-between">
              <button
                onClick={() => removeFolder(editingFolder.folder_id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:bg-red-400/10 transition-colors"
                data-testid="folder-edit-delete"
              >
                <Trash2 size={12} /> Delete folder
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingFolder(null)}
                  className="px-3 py-1.5 rounded-lg text-xs text-gray-400 hover:text-white transition-colors"
                >Cancel</button>
                <button
                  onClick={saveFolderEdit}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-teal-400 text-space-900 hover:bg-teal-500"
                  data-testid="folder-edit-save"
                >Save</button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* GHL Custom Menu Link generator — surfaces a templated URL the
          agency owner pastes into GoHighLevel Settings → Custom Menu Links. */}
      <GHLCustomMenuLinkModal
        open={showGHLMenuLinkModal}
        onClose={() => setShowGHLMenuLinkModal(false)}
        workspaceId={workspaceId}
      />
    </DashboardLayout>
  );
};

export default SubAccountsPage;
